/** @file Select value type definitions Handles both single and multiple selection value types */

/**
 * Represents the value type for select components
 *
 * - When Multiple is false: returns single value T or null
 * - When Multiple is true: returns array of T
 *
 * @template T The type of individual option values
 * @template Multiple Whether multiple selection is enabled
 */
export type HxSelectValue<T, Multiple extends boolean = false> = Multiple extends true ? T[] : T | null;

/** Type helper to extract the underlying value type from HxSelectValue */
export type UnwrapSelectValue<V> = V extends HxSelectValue<infer T, any> ? T : never;

/** Type guard to check if a value represents a multi-select array */
export function isMultiSelectValue<T>(value: any): value is T[] {
  return Array.isArray(value);
}

/** Type guard to check if a value represents a single selection */
export function isSingleSelectValue<T>(value: any): value is T | null {
  return !Array.isArray(value);
}

/**
 * Helper to normalize select value to always return an array Useful for uniform processing of both single and
 * multi-select
 */
export function normalizeToArray<T>(value: HxSelectValue<T, any>): T[] {
  if (value === null || value === undefined) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

/** Helper to check if a specific value is selected Works for both single and multi-select scenarios */
export function isValueSelected<T>(currentValue: HxSelectValue<T, any>, checkValue: T): boolean {
  const normalized = normalizeToArray(currentValue);
  return normalized.includes(checkValue);
}

/** Helper to toggle a value in multi-select mode Adds if not present, removes if present */
export function toggleMultiSelectValue<T>(currentValue: T[], toggleValue: T): T[] {
  const index = currentValue.indexOf(toggleValue);
  if (index === -1) {
    return [...currentValue, toggleValue];
  } else {
    return currentValue.filter((_, i) => i !== index);
  }
}
